import type { ReactNode } from 'react'
import type { LearnerProfileData } from '~/gql/users/learner-profile'
import { useTranslation } from 'react-i18next'
import { AccountStatusBadge } from '~/components/AccountStatusBadge'
import { tourAnchor } from '~/helpers/tour/anchors'
import { userDisplayName } from '~/helpers/users/display-name'
import { userInitials } from '~/helpers/users/initials'
import { roleLabel } from '~/helpers/users/role-label'

type ProfileHeaderProps = {
  profile: Pick<LearnerProfileData, 'firstName' | 'lastName' | 'email' | 'username' | 'roleName' | 'status'>
  /** Actions permises à l'appelant (écrire, consulter son espace). */
  actions?: ReactNode
}

/** En-tête de la fiche apprenant (#242) : qui, quel rôle, quel statut. */
function ProfileHeader({ profile, actions }: ProfileHeaderProps) {
  const { t } = useTranslation()

  return (
    <section
      className="flex flex-wrap items-center gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm"
      {...tourAnchor('profile.header')}
    >
      <div aria-hidden="true" className="flex size-14 items-center justify-center rounded-full bg-primary text-lg font-semibold text-primary-foreground">
        {userInitials(profile.firstName, profile.lastName, profile.username)}
      </div>
      {/* Une base de 24rem : sous elle, ce sont les actions qui passent à la ligne, jamais le nom qui s'écrase. */}
      <div className="min-w-0 flex-[1_1_24rem]">
        <h2 className="break-words text-xl font-semibold">{userDisplayName(profile)}</h2>
        {/* Sans nom ni e-mail, le pseudo est déjà le titre : pas de seconde ligne qui le répète. */}
        {userDisplayName(profile) !== (profile.email ?? profile.username) && (
          <p className="break-words text-sm text-muted-foreground">{profile.email ?? profile.username}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span>{roleLabel(t, profile.roleName)}</span>
          <AccountStatusBadge status={profile.status} />
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </section>
  )
}

export default ProfileHeader
